import React, { useState, useEffect, FC } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  useTheme,
  styled,
  Button,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Icon,
  IconButton,
  LinearProgress,
  Divider,
  FormControlLabel,
  Switch,
  SelectChangeEvent,
  InputAdornment,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Card,
  CardContent,
  Chip,
  Grid,
  alpha,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useError } from "../../../errors/useError";
import { useAuth, LoginOptions } from "../hooks/useAuth";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import {
  publicService,
  Business,
  User,
} from "../../../services/public.service";
import logo from "../../../assets/logo/text-logo.png";
import background from "../../../assets/auth/login.png";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  width: "100%",
  maxWidth: 1200,
  backdropFilter: "blur(10px)",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  minHeight: "85vh",
  overflow: "hidden",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    lineHeight: 1.5,
  },
}));

const KeyboardContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.1)",
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  zIndex: 1000,
  transition: "transform 0.3s ease-in-out",
}));

const UserCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "all 0.2s ease",
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  border: "1px solid",
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    borderColor: theme.palette.primary.main,
  },
  "&.selected": {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

interface PinDotProps {
  filled: boolean;
}

const PinDot = styled(Box)<PinDotProps>(({ theme, filled }) => ({
  width: 16,
  height: 16,
  borderRadius: "50%",
  border: `2px solid ${theme.palette.primary.main}`,
  backgroundColor: filled ? theme.palette.primary.main : "transparent",
  margin: "0 6px",
  transition: "all 0.2s ease",
}));

const PinButton = styled(Button)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 12,
  fontSize: 24,
  fontWeight: 600,
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: theme.palette.primary.main,
  },
  "&:active": {
    transform: "scale(0.98)",
  },
}));

// Define the steps for the login process
const steps = ["Select Store", "Select User", "Enter PIN"];

// Background Pattern Components
const GridPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: "64px 64px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const DotsPattern = () => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 2px, transparent 2px)`,
      backgroundSize: "32px 32px",
      opacity: 0.3,
      zIndex: 0,
    }}
  />
);

const CircleGlow = () => (
  <Box
    sx={{
      position: "absolute",
      width: "60%",
      paddingBottom: "60%",
      background:
        "radial-gradient(circle at center, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: 0,
      animation: "pulse 8s ease-in-out infinite",
      "@keyframes pulse": {
        "0%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
        "50%": {
          transform: "translate(-50%, -50%) scale(1.2)",
          opacity: 0.3,
        },
        "100%": {
          transform: "translate(-50%, -50%) scale(1)",
          opacity: 0.5,
        },
      },
    }}
  />
);

export const CashierLoginPage: FC = () => {
  const { login } = useAuth();
  const { throwError } = useError();
  const navigate = useNavigate();
  const theme = useTheme();

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [displayId, setDisplayId] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Virtual keyboard state
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardInput, setKeyboardInput] = useState("");
  const [activeInput, setActiveInput] = useState<"displayId" | "password" | "">(
    ""
  );
  const [keyboardEnabled, setKeyboardEnabled] = useState(
    localStorage.getItem("keyboardEnabled") === "true"
  );

  // For PIN entry
  const [pin, setPin] = useState("");
  const MAX_PIN_LENGTH = 5;

  // Load saved display ID if available
  useEffect(() => {
    const savedDisplayId = localStorage.getItem("storeDisplayId");
    if (savedDisplayId) {
      setDisplayId(savedDisplayId);
    }

    // Set initial keyboard state from localStorage
    const keyboardSetting = localStorage.getItem("keyboardEnabled");
    if (keyboardSetting !== null) {
      setKeyboardEnabled(keyboardSetting === "true");
    }
  }, []);

  // Handle display ID input change
  const handleDisplayIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayId(e.target.value);
  };

  // Handle searching for a business by display ID
  const handleSearchBusiness = async () => {
    if (!displayId || isNaN(Number(displayId))) {
      throwError({
        name: "ValidationError",
        message: "Please enter a valid store ID (numbers only)",
        type: "VALIDATION",
      });
      return;
    }

    try {
      setSearchLoading(true);
      const response = await publicService.searchBusinessesByDisplayId(
        Number(displayId)
      );
      setBusinesses(response.businesses);

      // If only one business is found, select it automatically
      if (response.businesses.length === 1) {
        handleSelectBusiness(response.businesses[0]);
      } else if (response.businesses.length === 0) {
        throwError({
          name: "ValidationError",
          message: "No stores found with that ID",
          type: "VALIDATION",
        });
      }
    } catch (error) {
      throwError({
        name: "Error",
        message:
          error instanceof Error ? error.message : "Failed to search for store",
        type: "VALIDATION",
      });
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle selecting a business
  const handleSelectBusiness = async (business: Business) => {
    setSelectedBusiness(business);

    // Save the display ID to localStorage
    localStorage.setItem("storeDisplayId", business.display_id.toString());

    // Fetch users for this business
    try {
      setUsersLoading(true);
      const usersResponse = await publicService.getUsersByStoreId(business.id, {
        is_pos_user: true,
        is_active: true,
        limit: 50,
      });
      setUsers(usersResponse.users);
      setActiveStep(1); // Move to user selection step
    } catch (error) {
      throwError({
        name: "Error",
        message:
          error instanceof Error ? error.message : "Failed to fetch users",
        type: "VALIDATION",
      });
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle selecting a user
  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    // Reset PIN when selecting a new user
    setPin("");
    setPassword("");
    setActiveStep(2); // Move to password entry step
  };

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  // Handle input focus for keyboard
  const handleInputFocus = (inputName: "displayId" | "password") => {
    if (keyboardEnabled) {
      setActiveInput(inputName);
      setKeyboardInput(inputName === "displayId" ? displayId : password);
      setShowKeyboard(true);
    }
  };

  // Handle keyboard input
  const handleKeyboardChange = (input: string) => {
    setKeyboardInput(input);
    if (activeInput === "displayId") {
      setDisplayId(input);
    } else if (activeInput === "password") {
      setPassword(input);
    }
  };

  // Handle PIN button press
  const handlePinButtonPress = (digit: string) => {
    if (pin.length < MAX_PIN_LENGTH) {
      const newPin = pin + digit;
      setPin(newPin);
      setPassword(newPin); // Update the main password state

      // Auto-submit when pin reaches 5 digits
      if (newPin.length === MAX_PIN_LENGTH) {
        setTimeout(() => {
          handleSubmit();
        }, 300); // Small delay for better UX
      }
    }
  };

  // Handle PIN backspace
  const handlePinBackspace = () => {
    if (pin.length > 0) {
      const newPin = pin.slice(0, -1);
      setPin(newPin);
      setPassword(newPin);
    }
  };

  // Handle PIN clear
  const handlePinClear = () => {
    setPin("");
    setPassword("");
  };

  // Handle form submission without event parameter
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // We should never reach this step without a user, but check anyway
    if (!selectedUser) {
      // Go back to user selection
      setActiveStep(1);
      throwError({
        name: "ValidationError",
        message: "Please select a user first",
        type: "VALIDATION",
      });
      return;
    }
    if (!password || password.length !== MAX_PIN_LENGTH) {
      // throwError({
      //   name: "ValidationError",
      //   message: "Please enter your 5-digit PIN",
      //   type: "VALIDATION",
      // });
      return;
    }

    submitLogin();
  };

  // Extracted login submission logic
  const submitLogin = async () => {
    try {
      setIsLoading(true);

      // Save business information for future use
      if (selectedBusiness) {
        localStorage.setItem("storeId", selectedBusiness.id);
        localStorage.setItem("storeName", selectedBusiness.business_name);
        localStorage.setItem(
          "storeDisplayId",
          selectedBusiness.display_id.toString()
        );
      }

      // Use non-null assertion because we've already checked in handleSubmit
      // that selectedUser exists
      const user = selectedUser!;

      // Login with POS context flag
      await login(user.email, password, { isPosLogin: true });
    } catch (error) {
      throwError({
        name: "AuthError",
        message: error instanceof Error ? error.message : "Login failed",
        type: "AUTHENTICATION",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle going back to previous step
  const handleBack = () => {
    if (activeStep === 0) {
      navigate("/auth/login");
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  // Check if the current step is complete
  const isStepComplete = () => {
    switch (activeStep) {
      case 0:
        return !!selectedBusiness;
      case 1:
        return !!selectedUser;
      case 2:
        return !!password;
      default:
        return false;
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    if (activeStep === 0 && !selectedBusiness) return 0;
    return ((activeStep + (isStepComplete() ? 1 : 0)) / steps.length) * 100;
  };

  // We'll type-guard the selectedUser before using it
  const getUserInitials = () => {
    if (!selectedUser) return "";
    return `${selectedUser.first_name.charAt(0)}${selectedUser.last_name.charAt(
      0
    )}`;
  };

  // Render store selection step
  const renderStoreSelection = () => (
    <>
      <StyledTextField
        fullWidth
        label="Store ID"
        value={displayId}
        onChange={handleDisplayIdChange}
        onFocus={() => handleInputFocus("displayId")}
        margin="normal"
        type="number"
        required
        disabled={searchLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSearchBusiness}
                disabled={!displayId || searchLoading}
                size="large"
                sx={{
                  color: "#06B6D4",
                  "&:hover": {
                    backgroundColor: "rgba(6, 182, 212, 0.04)",
                  },
                }}
              >
                {searchLoading ? (
                  <CircularProgress size={24} sx={{ color: "#06B6D4" }} />
                ) : (
                  <Icon>search</Icon>
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
          px: 1,
          py: 1.5,
          borderRadius: 1,
          backgroundColor: alpha("#06B6D4", 0.04),
          border: "1px solid",
          borderColor: alpha("#06B6D4", 0.1),
        }}
      >
        <Icon sx={{ color: "#06B6D4", fontSize: 20 }}>info</Icon>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "0.875rem",
            lineHeight: 1.5,
          }}
        >
          Don't have a Store ID? Contact your administrator
        </Typography>
      </Box>

      {businesses.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Store:
          </Typography>
          <List sx={{ width: "100%" }}>
            {businesses.map((business) => (
              <UserCard
                key={business.id}
                className={
                  selectedBusiness?.id === business.id ? "selected" : ""
                }
                onClick={() => handleSelectBusiness(business)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        {business.business_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {business.display_id}
                      </Typography>
                    </Box>
                    <Chip
                      label={business.is_active ? "Active" : "Inactive"}
                      color={business.is_active ? "success" : "default"}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </UserCard>
            ))}
          </List>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            textTransform: "none",
            borderColor: "#06B6D4",
            color: "#06B6D4",
            "&:hover": {
              borderColor: "#0891B2",
              backgroundColor: "rgba(6, 182, 212, 0.04)",
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => selectedBusiness && setActiveStep(1)}
          disabled={!selectedBusiness || searchLoading}
          sx={{
            textTransform: "none",
            background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
            color: "white",
            "&:hover": {
              background: "linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)",
            },
            "&:disabled": {
              background: "linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)",
            },
          }}
        >
          Next
        </Button>
      </Box>
    </>
  );

  // Render user selection step
  const renderUserSelection = () => (
    <>
      {usersLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Your Account:
          </Typography>
          <List sx={{ width: "100%" }}>
            {users.map((user) => (
              <UserCard
                key={user.id}
                className={selectedUser?.id === user.id ? "selected" : ""}
                onClick={() => handleSelectUser(user)}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      {user.first_name.charAt(0)}
                      {user.last_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {user.first_name} {user.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </UserCard>
            ))}
          </List>
        </Box>
      ) : (
        <Box sx={{ my: 3, textAlign: "center" }}>
          <Icon
            style={{
              fontSize: 48,
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            person_off
          </Icon>
          <Typography variant="h6">No users found</Typography>
          <Typography variant="body2" color="text.secondary">
            No cashiers are available for this store
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            textTransform: "none",
            borderColor: "#06B6D4",
            color: "#06B6D4",
            "&:hover": {
              borderColor: "#0891B2",
              backgroundColor: "rgba(6, 182, 212, 0.04)",
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => selectedUser && setActiveStep(2)}
          disabled={!selectedUser}
          sx={{
            textTransform: "none",
            background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
            color: "white",
            "&:hover": {
              background: "linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)",
            },
            "&:disabled": {
              background: "linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)",
            },
          }}
        >
          Next
        </Button>
      </Box>
    </>
  );

  // Render password entry step with PIN pad
  const renderPasswordEntry = React.useMemo(() => {
    if (!selectedUser) return null;

    return (
      <form onSubmit={handleSubmit}>
        {/* PIN entry UI */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            Enter your 5-digit PIN
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            {[...Array(MAX_PIN_LENGTH)].map((_, i) => (
              <PinDot key={i} filled={i < pin.length} />
            ))}
          </Box>

          <Box
            sx={{
              mt: 4,
              width: "100%",
              maxWidth: 280,
              mx: "auto",
            }}
          >
            <Grid container justifyContent="center" spacing={1}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <Grid item key={num} xs={4}>
                  <PinButton
                    variant="outlined"
                    onClick={() => handlePinButtonPress(num.toString())}
                    disabled={isLoading}
                  >
                    {num}
                  </PinButton>
                </Grid>
              ))}
              <Grid item xs={4}>
                <PinButton
                  variant="outlined"
                  onClick={handlePinClear}
                  disabled={isLoading}
                  color="error"
                >
                  <Icon>clear</Icon>
                </PinButton>
              </Grid>
              <Grid item xs={4}>
                <PinButton
                  variant="outlined"
                  onClick={() => handlePinButtonPress("0")}
                  disabled={isLoading}
                >
                  0
                </PinButton>
              </Grid>
              <Grid item xs={4}>
                <PinButton
                  variant="outlined"
                  onClick={handlePinBackspace}
                  disabled={isLoading}
                >
                  <Icon>backspace</Icon>
                </PinButton>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={isLoading}
            sx={{
              textTransform: "none",
              borderColor: "#06B6D4",
              color: "#06B6D4",
              "&:hover": {
                borderColor: "#0891B2",
                backgroundColor: "rgba(6, 182, 212, 0.04)",
              },
            }}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || pin.length !== MAX_PIN_LENGTH}
            sx={{
              textTransform: "none",
              background: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(135deg, #0891B2 0%, #06B6D4 100%)",
              },
              "&:disabled": {
                background: "linear-gradient(135deg, #CBD5E1 0%, #94A3B8 100%)",
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Login"
            )}
          </Button>
        </Box>
      </form>
    );
  }, [
    selectedUser,
    pin.length,
    isLoading,
    theme.palette.primary.main,
    handleBack,
    handleSubmit,
    handlePinButtonPress,
    handlePinClear,
    handlePinBackspace,
  ]);

  // Updated getStepContent to always return a valid React node
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderStoreSelection();
      case 1:
        return renderUserSelection();
      case 2:
        if (!selectedUser) {
          return (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Please select a user first</Typography>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(1)}
                sx={{ mt: 2 }}
              >
                Go Back
              </Button>
            </Box>
          );
        }
        return renderPasswordEntry || null;
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* Background Patterns */}
      <GridPattern />
      <DotsPattern />
      <CircleGlow />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          background:
            "radial-gradient(circle at 20% 20%, rgba(6, 182, 212, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.15) 0%, transparent 40%)",
          zIndex: 0,
          animation: "float 15s ease-in-out infinite",
          "@keyframes float": {
            "0%": {
              transform: "translateY(0) scale(1)",
            },
            "50%": {
              transform: "translateY(-20px) scale(1.05)",
            },
            "100%": {
              transform: "translateY(0) scale(1)",
            },
          },
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, sm: 3 },
        }}
      >
        <StyledPaper elevation={4}>
          {/* Left Side - Form Content */}
          <Box
            sx={{
              flex: "1 1 50%",
              p: { xs: 3, sm: 4, md: 6 },
              display: "flex",
              flexDirection: "column",
              position: "relative",
              zIndex: 1,
              height: "100%",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                width: "80%",
                maxWidth: "200px",
                mb: 4,
                alignSelf: "center",
              }}
            >
              <img
                src={logo}
                alt="VenFlows"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                fontSize: { xs: "1.75rem", md: "2rem" },
                mb: 1,
              }}
            >
              Cashier Login
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 4,
              }}
            >
              {activeStep === 0
                ? "Enter your store ID to get started"
                : activeStep === 1
                ? "Select your user account"
                : "Enter your PIN to continue"}
            </Typography>

            {/* Progress Stepper */}
            <Box sx={{ mb: 4, width: "100%" }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  "& .MuiStepLabel-root .Mui-completed": {
                    color: "#06B6D4",
                  },
                  "& .MuiStepLabel-root .Mui-active": {
                    color: "#06B6D4",
                  },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Step Content */}
            <Box sx={{ flex: 1 }}>{getStepContent(activeStep)}</Box>
          </Box>

          {/* Right Side - Background Image */}
          <Box
            sx={{
              flex: "1 1 50%",
              position: "relative",
              display: { xs: "none", lg: "block" },
              background: "linear-gradient(to top, #00333F 0%, #0891B2 100%)",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={background}
              alt="Login background"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                // background: "rgba(0, 0, 0, 0.2)",
                // backdropFilter: "blur(1px)",
                userSelect: "none",
                pointerEvents: "none",
              }}
            />
          </Box>
        </StyledPaper>
      </Container>

      {/* Virtual Keyboard */}
      {showKeyboard && keyboardEnabled && (
        <KeyboardContainer
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            transform: showKeyboard ? "translateY(0)" : "translateY(100%)",
            background: (theme) => alpha(theme.palette.background.paper, 0.95),
            borderTop: "1px solid",
            borderColor: "divider",
            backdropFilter: "blur(10px)",
            zIndex: 1200,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Virtual Keyboard
            </Typography>
            <IconButton onClick={() => setShowKeyboard(false)} size="small">
              <Icon>close</Icon>
            </IconButton>
          </Box>
          <Keyboard
            onChange={handleKeyboardChange}
            inputName={activeInput}
            layoutName={activeInput === "password" ? "default" : "default"}
            value={keyboardInput}
            theme="hg-theme-default"
            display={{
              "{bksp}": "⌫",
              "{enter}": "Enter",
              "{space}": " ",
              "{shift}": "⇧",
            }}
          />
        </KeyboardContainer>
      )}
    </Box>
  );
};
